import { useRef, useEffect, useState } from 'react';
import {
  Box,
  Select,
  MenuItem,
  Typography,
  FormControl,
} from '@forgeglobal/design-framework';
import { ReportType } from '@tcs/client-portal-api/types/ReportType';
import { useStyles } from './style';

interface ReportTypeSelectorProps {
  selectedReportType: ReportType;
  includeSelectAllOption?: boolean;
  onReportTypeChange: (reportType: ReportType) => void;
}

const REPORT_OPTIONS = [
  {
    value: ReportType.ALL,
    title: ReportType.ALL,
    description: 'View all available reports across all report types.',
  },
  {
    value: ReportType.ACCOUNTS,
    title: ReportType.ACCOUNTS,
    description:
      'Provides a comprehensive view of each investor account, including balances, fee status, RMD details, and address indicators.',
  },
  {
    value: ReportType.POSITIONS,
    title: ReportType.POSITIONS,
    description:
      'Shows detailed investment holdings per account, including units, valuation/FMV info, and distress indicators.',
  },
  {
    value: ReportType.TRANSACTION,
    title: ReportType.TRANSACTION,
    description:
      'Displays all transaction activity across accounts, with added visibility into asset IDs and recurring distribution requests.',
  },
  {
    value: ReportType.BENEFICIARY,
    title: ReportType.BENEFICIARY,
    description:
      'Outlines account beneficiary details including names, types, levels, and share percentages.',
  },
];

const ReportTypeSelector = ({
  selectedReportType,
  includeSelectAllOption = false,
  onReportTypeChange,
}: ReportTypeSelectorProps) => {
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [paperWidth, setPaperWidth] = useState<number>(0);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.clientWidth;
        if (width > 0) {
          setPaperWidth(width);
        }
      }
    };

    const timeoutId = setTimeout(updateWidth, 0);
    window.addEventListener('resize', updateWidth);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  return (
    <Box ref={containerRef} width="100%">
      <FormControl fullWidth className={classes.placeholder}>
        <Select
          fullWidth
          label="Report type"
          id="report-type"
          aria-label="Report type"
          onDelete={null}
          value={selectedReportType}
          onChange={(e) => onReportTypeChange(e.target.value as ReportType)}
          MenuProps={{
            PaperProps: {
              style: {
                width: paperWidth > 0 ? `${paperWidth}px` : 'fit-content',
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
          }}>
          {(includeSelectAllOption
            ? REPORT_OPTIONS
            : REPORT_OPTIONS.slice(1)
          ).map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              className={classes.menuItem}>
              <Typography variant="body2" color="text.primary">
                {option.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {option.description}
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default ReportTypeSelector;
